/**
 * Telling an empty rich-text document from a stored empty value.
 *
 * ## The bug this exists to stop
 *
 * TipTap has no concept of "no content". Hand `CCRichText` an empty string and
 * the document it builds serialises back as `<p></p>`, so the editor returns
 * something where the server stored nothing. That reaches `onChange`, patches
 * the draft, and simply OPENING an event with no notes marks it as edited:
 * "Unsaved changes" over an event nobody has touched, Submit blocked, Discard
 * offered.
 *
 * On the Permissions tab it is worse than cosmetic. Terms and conditions live
 * under `Approval.*`, and every `Approval.*` path is material to a sign-off, so
 * an untouched event could lose its approvals on the next save because an
 * editor said `<p></p>` where Mongo said null.
 *
 * ## Why not compare in `deepEqual` instead
 *
 * Because `""`, `null` and `<p></p>` are only interchangeable in a rich-text
 * field. Teaching the document-wide comparison that empty things are equal
 * would hide real edits everywhere else.
 */

/** Markup that carries content even with no text in it. */
const MEDIA = /<(img|video|iframe|hr)\b/i;

/** Is this an empty document, however TipTap chose to spell it? */
export function isEmptyRichText(html: string | null | undefined): boolean {
	if (!html) return true;
	if (MEDIA.test(html)) return false;
	return (
		html
			.replace(/<[^>]*>/g, "")
			.replace(/&nbsp;/gi, " ")
			.trim() === ""
	);
}

/**
 * Did the editor hand back what it was given?
 *
 * `handed` is the value passed to `CCRichText`, not the stored one, which
 * matters where the two differ: the Permissions tab promotes legacy plain text
 * to paragraphs on the way in, so the editor's first emission legitimately
 * differs from what is in the database and comparing against the database would
 * report an edit on every open.
 */
export function sameRichText(
	incoming: string | null | undefined,
	handed: string | null | undefined,
): boolean {
	if (incoming === handed) return true;
	return isEmptyRichText(incoming) && isEmptyRichText(handed);
}

/**
 * Terms saved before this field became rich text are plain strings, and their
 * line breaks are meaningful — a numbered list of conditions, typically.
 * CCRichText parses its value as HTML, which would collapse those breaks into
 * one run-on paragraph and then save the flattened version back over the top.
 *
 * So plain text is promoted to paragraphs on the way in. The tag test is
 * deliberately loose: anything already containing markup is passed through
 * untouched and left to the editor.
 */
export const asHtml = (value: string): string => {
	if (!value) return "";
	if (/<[a-z][\s\S]*>/i.test(value)) return value;
	const escape = (text: string) =>
		text
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;");
	return value
		.split(/\n{2,}/)
		.map((block) => `<p>${escape(block).replace(/\n/g, "<br>")}</p>`)
		.join("");
};
